import { autobind } from "core-decorators";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { MapboxBuildingObject } from "../Objects/MapboxBuildingObject";
import { MAPBOX_MIN_ZOOM } from "@/constant";

class BuildingEntity {
  private _mapboxViewer: MapboxViewer;
  private _records: Map<string, MapboxBuildingObject> = new Map();
  private _sourceId: string = "building-source";
  private _clearanceSourceId: string = "building-clearance-source";
  private _clearanceLineSourceId: string = "building-clearance-line-source";
  private _geojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  private _clearanceGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  private _clearanceLineGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  private _savedLayerVisibility = new Map<string, string>();
  private readonly _layerIds = [
    "building-polygons",
    "building-polygons-outline",
    "building-extrusion",
    "building-models",
    "building-clearance-fill",
    "building-clearance-lines",
    "building-rotation-handles",
  ];

  constructor(mapboxViewer: MapboxViewer) {
    this._mapboxViewer = mapboxViewer;
    this.init();
  }

  @autobind
  init() {
    // Create building source
    this.dispose();
    this._mapboxViewer._mapViewer.addSource(this._sourceId, {
      type: "geojson",
      data: this._geojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Clearance sources
    this._mapboxViewer._mapViewer.addSource(this._clearanceSourceId, {
      type: "geojson",
      data: this._clearanceGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });
    this._mapboxViewer._mapViewer.addSource(this._clearanceLineSourceId, {
      type: "geojson",
      data: this._clearanceLineGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Add polygon fill layer for interaction
    this._mapboxViewer._mapViewer.addLayer({
      id: "building-polygons",
      type: "fill",
      source: this._sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "building"],
        ["==", ["geometry-type"], "Polygon"],
      ],
      layout: {
        visibility: "visible",
      },
      paint: {
        "fill-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#ff0000",
          ["boolean", ["feature-state", "highlight"], false],
          "#ffff00",
          ["get", "color"],
        ],
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          0.3,
          ["boolean", ["feature-state", "highlight"], false],
          0.2,
          0.01,
        ],
      },
    });

    // Add polygon outline layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "building-polygons-outline",
      type: "line",
      source: this._sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "building"],
        // ["==", ["geometry-type"], "Polygon"],
      ],
      layout: {
        visibility: "none",
      },
      paint: {
        "line-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#ff0000",
          ["boolean", ["feature-state", "highlight"], false],
          "#ffff00",
          ["get", "color"],
        ],
        "line-width": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          3,
          ["boolean", ["feature-state", "highlight"], false],
          2,
          1,
        ],
      },
    });

    // Add fill-extrusion layer for 3D building visualization
    // IMPORTANT: This layer MUST filter for Polygon geometry type
    const extrusionLayer = {
      id: "building-extrusion",
      type: "fill-extrusion",
      source: this._sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "building"],
        ["==", ["geometry-type"], "Polygon"], // CRITICAL: fill-extrusion only works with Polygons
        ["==", ["get", "visible"], true],
        [">", ["get", "height"], 0],
      ],
      paint: {
        "fill-extrusion-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#ff0000", // Red when selected
          ["boolean", ["feature-state", "highlight"], false],
          "#ffff00", // Yellow when highlighted
          ["get", "color"], // Default building color
        ],
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-base": ["get", "base_height"],
        "fill-extrusion-opacity": 0.9,
      },
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Mapbox filter types don't support complex nested expressions
    this._mapboxViewer._mapViewer.addLayer(extrusionLayer);

    // Clearance area fill layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "building-clearance-fill",
      type: "fill",
      source: this._clearanceSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true],
        [">", ["get", "clearanceDistance"], 0],
        ["==", ["get", "showClearanceArea"], true],
      ],
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.15,
      },
    });

    // Clearance lines layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "building-clearance-lines",
      type: "line",
      source: this._clearanceLineSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true],
        [">", ["get", "clearanceDistance"], 0],
        ["==", ["get", "showClearanceArea"], true],
      ],
      paint: {
        "line-color": ["get", "color"],
        "line-width": 2,
        "line-opacity": 0.9,
      },
    });

    // Add rotation handle layer
    const rotationHandleLayer = {
      id: "building-rotation-handles",
      type: "circle",
      source: this._sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "building"],
        ["==", ["get", "subtype"], "rotation_handle"],
        ["==", ["get", "visible"], true],
        ["==", ["get", "isSelected"], true],
      ],
      layout: {
        visibility: "visible",
      },
      paint: {
        "circle-radius": 6,
        "circle-color": ["get", "color"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Mapbox filter types don't support complex nested expressions
    this._mapboxViewer._mapViewer.addLayer(rotationHandleLayer);

    // Add 3D model layer for buildings
    const buildingModelsLayer = {
      id: "building-models",
      type: "model",
      source: this._sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "building"],
        ["==", ["get", "visible"], true],
      ],
      layout: {
        visibility: "visible",
      },
      paint: {
        "model-scale": ["get", "scale"],
        "model-rotation": ["get", "rotation"],
        "model-color": ["get", "color"],
      },
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Mapbox filter types don't support complex nested expressions
    this._mapboxViewer._mapViewer.addLayer(buildingModelsLayer);

    // Add interaction for building selection on polygons
    this._mapboxViewer._mapViewer.on("click", "building-polygons", (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setSelected();
          }
        }
      }
    });

    // Add hover interaction on polygons
    this._mapboxViewer._mapViewer.on("mouseenter", "building-polygons", (e) => {
      this._mapboxViewer._mapViewer.getCanvas().style.cursor = "pointer";
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setHighlight();
          }
        }
      }
    });

    this._mapboxViewer._mapViewer.on("mouseleave", "building-polygons", (e) => {
      this._mapboxViewer._mapViewer.getCanvas().style.cursor = "";
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setUnhighlight();
          }
        }
      }
    });

    // Add interaction for building selection on extrusions
    this._mapboxViewer._mapViewer.on("click", "building-extrusion", (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setSelected();
          }
        }
      }
    });

    // Add hover interaction on extrusions
    this._mapboxViewer._mapViewer.on(
      "mouseenter",
      "building-extrusion",
      (e) => {
        this._mapboxViewer._mapViewer.getCanvas().style.cursor = "pointer";
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const buildingId = feature.properties?.contentId;
          if (buildingId) {
            const building = this._records.get(buildingId);
            if (building) {
              building.setHighlight();
            }
          }
        }
      }
    );

    this._mapboxViewer._mapViewer.on(
      "mouseleave",
      "building-extrusion",
      (e) => {
        this._mapboxViewer._mapViewer.getCanvas().style.cursor = "";
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const buildingId = feature.properties?.contentId;
          if (buildingId) {
            const building = this._records.get(buildingId);
            if (building) {
              building.setUnhighlight();
            }
          }
        }
      }
    );

    // Add interaction for building selection on 3D models
    this._mapboxViewer._mapViewer.on("click", "building-models", (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setSelected();
          }
        }
      }
    });

    // Add hover interaction on 3D models
    this._mapboxViewer._mapViewer.on("mouseenter", "building-models", (e) => {
      this._mapboxViewer._mapViewer.getCanvas().style.cursor = "pointer";
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setHighlight();
          }
        }
      }
    });

    this._mapboxViewer._mapViewer.on("mouseleave", "building-models", (e) => {
      this._mapboxViewer._mapViewer.getCanvas().style.cursor = "";
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const buildingId = feature.properties?.contentId;
        if (buildingId) {
          const building = this._records.get(buildingId);
          if (building) {
            building.setUnhighlight();
          }
        }
      }
    });
  }

  @autobind
  add(buildingObject: MapboxBuildingObject) {
    this._records.set(buildingObject.contentId, buildingObject);
    this.updateSource();

    // Load the model
    buildingObject.loadModel();
  }

  @autobind
  remove(buildingId: string) {
    const building = this._records.get(buildingId);
    if (building) {
      // building.dispose();
      this._records.delete(buildingId);
      this.updateSource();
    }
  }
  getById(buildingId: string): MapboxBuildingObject | undefined {
    return this._records.get(buildingId);
  }
  @autobind
  updateSource() {
    const features: GeoJSON.Feature[] = [];

    this._records.forEach((building) => {
      const feature = building.getFeature();
      if (feature) {
        features.push(feature);
      }
      // Add polygon feature for interaction
      const polygonFeature = building.getPolygonFeature();
      if (polygonFeature) {
        features.push(polygonFeature);
      }
      // Add rotation handle feature
      const rotationHandle = building.getRotationHandleFeature();
      if (rotationHandle) {
        features.push(rotationHandle);
      }
    });

    this._geojson.features = features;

    const source = this._mapboxViewer._mapViewer.getSource(
      this._sourceId
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(this._geojson);
    }

    // Collect clearance features separately
    const clearanceAreaFeatures: GeoJSON.Feature[] = [];
    const clearanceLineFeatures: GeoJSON.Feature[] = [];
    this._records.forEach((building) => {
      const area = building.getClearanceAreaFeature?.();
      if (area) clearanceAreaFeatures.push(area);
      const lines = building.getClearanceLinesFeatures?.() || [];
      if (lines.length)
        clearanceLineFeatures.push(...(lines as GeoJSON.Feature[]));
    });

    this._clearanceGeojson.features = clearanceAreaFeatures;
    this._clearanceLineGeojson.features = clearanceLineFeatures;

    const clearanceSource = this._mapboxViewer._mapViewer.getSource(
      this._clearanceSourceId
    ) as mapboxgl.GeoJSONSource;
    if (clearanceSource) {
      clearanceSource.setData(this._clearanceGeojson);
    }
    const clearanceLineSource = this._mapboxViewer._mapViewer.getSource(
      this._clearanceLineSourceId
    ) as mapboxgl.GeoJSONSource;
    if (clearanceLineSource) {
      clearanceLineSource.setData(this._clearanceLineGeojson);
    }
  }

  @autobind
  getBuilding(buildingId: string): MapboxBuildingObject | undefined {
    return this._records.get(buildingId);
  }

  @autobind
  getAllBuildings(): MapboxBuildingObject[] {
    return Array.from(this._records.values());
  }

  /** Hide building layers while the simshady mesh overlay is shown. */
  @autobind
  setShadingOverlayActive(active: boolean) {
    const map = this._mapboxViewer._mapViewer;

    if (active) {
      for (const layerId of this._layerIds) {
        if (!map.getLayer(layerId)) continue;
        this._savedLayerVisibility.set(
          layerId,
          map.getLayoutProperty(layerId, "visibility") as string,
        );
        map.setLayoutProperty(layerId, "visibility", "none");
      }

      for (const building of this._records.values()) {
        building.setShadingOverlaySuppressed(true);
      }
      return;
    }

    for (const layerId of this._layerIds) {
      if (!map.getLayer(layerId)) continue;
      const saved = (this._savedLayerVisibility.get(layerId) ?? "visible") as
        | "none"
        | "visible";
      map.setLayoutProperty(layerId, "visibility", saved);
    }
    this._savedLayerVisibility.clear();

    for (const building of this._records.values()) {
      building.setShadingOverlaySuppressed(false);
    }
  }

  @autobind
  dispose() {
    const layers = [
      "building-clearance-lines",
      "building-clearance-fill",
      "building-models",
      "building-rotation-handles",
      "building-extrusion",
      "building-polygons-outline",
      "building-polygons",
    ];
    layers.forEach((layerId) => {
      if (this._mapboxViewer._mapViewer.getLayer(layerId)) {
        this._mapboxViewer._mapViewer.removeLayer(layerId);
      }
    });

    const sources = [
      this._sourceId,
      this._clearanceSourceId,
      this._clearanceLineSourceId,
    ];
    sources.forEach((sourceId) => {
      if (this._mapboxViewer._mapViewer.getSource(sourceId)) {
        this._mapboxViewer._mapViewer.removeSource(sourceId);
      }
    });

    this.clear();
  }

  @autobind
  clear() {
    this._records.forEach((building) => {
      building.dispose();
    });
    this._records.clear();
    this.updateSource();
  }
}

export default BuildingEntity;
